import { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, Platform, ScrollView, Pressable } from 'react-native';
import { useLocalSearchParams, router } from 'expo-router';
import Head from 'expo-router/head';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CheckCircle2, Link2Off } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY, BORDER_RADIUS, CONTENT_MAX_WIDTH } from '../src/theme';
import {
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Input,
  LoadingSpinner,
  RediLogo,
  TextLink,
} from '@components/common';
import {
  getPlanUpdateContext,
  submitPlanUpdate,
  type PlanUpdateContext,
  type PlanUpdateLinkError,
  type PlanUpdateStatus,
} from '../src/api/planUpdateLink';
import { createPrefixedLogger } from '@utils/logger';

const log = createPrefixedLogger('planUpdateScreen');

const NOTE_MAX = 500;

// Each task offers exactly these three, mapped straight onto the server's
// settable statuses (functions/src/plans/planUpdateLink.ts SETTABLE_STATUSES
// — 'asked' is the owner's marker, not something the party picks).
const STATUS_OPTIONS: { status: PlanUpdateStatus; label: string }[] = [
  { status: 'done', label: 'Done' },
  { status: 'accepted', label: 'Working on it' },
  { status: 'declined', label: 'Not ours' },
];

type Phase = 'loading' | 'error' | 'ready' | 'submitted';

// The person opening this link may never have seen REDI, and the page has no
// nav, so every phase carries the wordmark to say whose page it is.
function UpdatePageHeader() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  return (
    <View
      style={[
        styles.header,
        { paddingTop: insets.top + SPACING.md, borderBottomColor: colors.border },
      ]}
      testID="plan-update-header">
      <View style={styles.headerInner}>
        <RediLogo fontSize={20} />
      </View>
    </View>
  );
}

/**
 * The token from the link, held outside the component so it survives the
 * remount that clearing it from the URL can cause. One page, one visit, one
 * token: there is nothing here to leak between sessions, and it is cleared
 * when the screen unmounts.
 */
let capturedToken: string | null = null;

/** `#t=<token>`, the shape planUpdateLinkUrl builds. Web only. */
function readTokenFromHash(): string | null {
  try {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return null;
    const value = new URLSearchParams(hash).get('t');
    return value || null;
  } catch {
    return null;
  }
}

/**
 * Copy per failure kind. Never mentions an email address — the person on
 * this page has no account, and a support address here would read as an
 * invitation to reply to a no-reply link.
 */
function describeLinkError(error: PlanUpdateLinkError): { title: string; message: string } {
  switch (error.kind) {
    case 'expired':
      return { title: 'This link has expired.', message: 'Ask the plan owner for a new one.' };
    case 'replaced':
      return {
        title: 'This link has been replaced.',
        message: 'The plan owner sent a newer link. Use that one, or ask them to resend it.',
      };
    case 'invalid':
      return {
        title: "This link isn't valid.",
        message: 'Check that you copied the whole link, or ask the plan owner to resend it.',
      };
    case 'not-found':
      return {
        title: 'This plan is gone.',
        message: 'It may have been removed. Ask the plan owner what happened.',
      };
    case 'malformed':
      return {
        title: 'This link is missing something.',
        message: 'Open it again from the email you were sent, or ask for a new one.',
      };
    case 'unknown':
      return { title: 'Something went wrong.', message: error.message };
  }
}

/**
 * /update?t=<token> — the page an assigned community leader (a fire safe
 * council, a county office) lands on from an email to report progress on
 * their assigned defense-plan tasks. They have no REDI account and never
 * will; the signed token in the URL is their entire authority, so it's held
 * in a ref and stripped from the visible URL on mount rather than kept as a
 * route param — it must not linger in browser history, a screenshot, or the
 * Referer header of whatever the page links out to.
 */
export default function PlanUpdateScreen() {
  const { t } = useLocalSearchParams<{ t?: string }>();
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();

  // The token arrives in the URL fragment (see planUpdateToken.ts for why),
  // which expo-router does not hand to useLocalSearchParams. `t` stays as a
  // fallback for any link that predates the change.
  if (typeof t === 'string' && t) capturedToken = t;
  if (!capturedToken && Platform.OS === 'web') {
    capturedToken = readTokenFromHash();
  }
  const tokenRef = useRef<string | null>(capturedToken);

  // Take the token out of the address bar, so it is not left in history or
  // in a screenshot of somebody's screen.
  //
  // This has to outlast the router. Stripping once on mount does not hold:
  // expo-router writes the URL again from its own route state a beat later
  // and restores whatever was there, which is why the first two attempts at
  // this (history.replaceState on mount, then router.setParams) both looked
  // right in the code and did nothing in a real build. So: strip, then check
  // again a few times over the next second and a half, and stop as soon as
  // it stays clean. Bounded, and it gives up rather than spinning.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!tokenRef.current) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const strip = () => {
      try {
        if (window.location.hash || window.location.search) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      } catch (error) {
        log.warn('Could not strip the token from the URL:', error);
      }
    };
    strip();
    for (const delay of [0, 250, 750, 1500]) timers.push(setTimeout(strip, delay));
    return () => timers.forEach(clearTimeout);
  }, []);

  const [phase, setPhase] = useState<Phase>('loading');
  const [linkError, setLinkError] = useState<PlanUpdateLinkError | null>(null);
  const [context, setContext] = useState<PlanUpdateContext | null>(null);
  const [selections, setSelections] = useState<Record<string, PlanUpdateStatus>>({});
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [updatedCount, setUpdatedCount] = useState(0);

  // Not left lying around once the page is closed.
  useEffect(
    () => () => {
      capturedToken = null;
    },
    []
  );

  useEffect(() => {
    const token = tokenRef.current;
    if (!token) {
      setLinkError({ kind: 'malformed' });
      setPhase('error');
      return;
    }
    let cancelled = false;
    getPlanUpdateContext(token)
      .then((ctx) => {
        if (cancelled) return;
        setContext(ctx);
        setPhase('ready');
      })
      .catch((error: PlanUpdateLinkError) => {
        if (cancelled) return;
        setLinkError(error);
        setPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasSelection = Object.keys(selections).length > 0;

  const handleSelect = (assignmentId: string, status: PlanUpdateStatus) => {
    setSelections((prev) => ({ ...prev, [assignmentId]: status }));
  };

  const handleSubmit = async () => {
    const token = tokenRef.current;
    if (!token || !hasSelection) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const updates = Object.entries(selections).map(([assignmentId, status]) => ({
        assignmentId,
        status,
      }));
      const trimmedNote = note.trim();
      const result = await submitPlanUpdate({
        token,
        updates,
        note: trimmedNote ? trimmedNote : undefined,
      });
      setUpdatedCount(result.updated);
      setPhase('submitted');
    } catch (error) {
      const linkErr = error as PlanUpdateLinkError;
      if (linkErr.kind === 'unknown') {
        // Not a token problem (e.g. today's rate limit) — stay on the form
        // and let them try again rather than bouncing to a dead-end screen.
        setSubmitError(linkErr.message);
      } else {
        // The token stopped resolving between load and submit — the task
        // list on screen is stale, so treat it the same as a failed load.
        setLinkError(linkErr);
        setPhase('error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const planHref = useMemo(
    () => (context ? { pathname: '/plan/[id]' as const, params: { id: context.planId } } : null),
    [context]
  );

  if (phase === 'loading') {
    return (
      <View
        style={[styles.container, { backgroundColor: colors.background }]}
        testID="plan-update-screen">
        <Head>
          <title>Report progress - Redi</title>
        </Head>
        <UpdatePageHeader />
        <View style={styles.centeredWrap}>
          <LoadingSpinner />
        </View>
      </View>
    );
  }

  if (phase === 'error') {
    const description = linkError
      ? describeLinkError(linkError)
      : describeLinkError({ kind: 'unknown', message: 'Something went wrong.' });
    return (
      <View
        style={[styles.container, { backgroundColor: colors.background }]}
        testID="plan-update-screen">
        <Head>
          <title>Report progress - Redi</title>
        </Head>
        <UpdatePageHeader />
        <View style={styles.centeredWrap} testID="plan-update-error">
          <EmptyState icon={Link2Off} title={description.title} message={description.message} />
          {/* This page has no nav by design, so a dead link would otherwise
              be a dead end for somebody who has never seen REDI before. */}
          <View style={styles.doneLinks}>
            <TextLink label="See what REDI is" onPress={() => router.push('/feed')} />
          </View>
        </View>
      </View>
    );
  }

  if (phase === 'submitted') {
    return (
      <View
        style={[styles.container, { backgroundColor: colors.background }]}
        testID="plan-update-screen">
        <Head>
          <title>Update sent - Redi</title>
        </Head>
        <UpdatePageHeader />
        <View style={styles.centeredWrap} testID="plan-update-submitted">
          <CheckCircle2 size={32} color={colors.action} />
          <Text style={[styles.doneTitle, { color: colors.text }]}>Thanks for the update.</Text>
          <Text style={[styles.doneBody, { color: colors.textSecondary }]}>
            {`We sent your update on ${updatedCount} ${updatedCount === 1 ? 'task' : 'tasks'} to the group.`}
          </Text>
          <View style={styles.doneLinks}>
            {planHref && (
              <TextLink label="See the plan on the map" onPress={() => router.push(planHref)} />
            )}
            <TextLink label="See what is happening nearby" onPress={() => router.push('/feed')} />
          </View>
        </View>
      </View>
    );
  }

  // phase === 'ready'
  if (!context) return null;

  return (
    <View
      style={[styles.container, { backgroundColor: colors.background }]}
      testID="plan-update-screen">
      <Head>
        <title>Report progress - Redi</title>
      </Head>
      <UpdatePageHeader />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: insets.bottom + SPACING.xl },
        ]}>
        <View style={styles.constraint}>
          <Text style={[styles.planTitle, { color: colors.text }]}>{context.planTitle}</Text>
          <Text style={[styles.partyLine, { color: colors.textSecondary }]}>
            You are listed as {context.partyName}.
          </Text>

          {context.tasks.map((task) => (
            <Card
              key={task.assignmentId}
              variant="outlined"
              padding="md"
              style={styles.taskCard}
              testID={`plan-update-task-${task.assignmentId}`}>
              <Text style={[styles.taskName, { color: colors.text }]}>{task.featureName}</Text>
              {task.description ? (
                <Text style={[styles.taskDescription, { color: colors.textSecondary }]}>
                  {task.description}
                </Text>
              ) : null}
              <View style={styles.optionsRow} accessibilityRole="radiogroup">
                {STATUS_OPTIONS.map((option) => {
                  const selected = selections[task.assignmentId] === option.status;
                  return (
                    <Pressable
                      key={option.status}
                      onPress={() => handleSelect(task.assignmentId, option.status)}
                      // Suffixed with the task: a party with two assignments
                      // renders this row twice, and an audit that wants one
                      // of them needs to be able to say which.
                      testID={`plan-update-status-${option.status}-${task.assignmentId}`}
                      accessibilityRole="radio"
                      // RN-web drops accessibilityState on export, so the raw
                      // aria-* prop is what axe and screen readers actually
                      // see (same pattern as MapLayersSheet's basemap rows
                      // and NotifyAgentSheet's status picker).
                      aria-checked={selected}
                      accessibilityLabel={`${option.label} for ${task.featureName}`}
                      style={[
                        styles.optionPill,
                        {
                          borderColor: selected ? colors.action : colors.border,
                          backgroundColor: selected ? colors.action : 'transparent',
                        },
                      ]}>
                      <Text
                        style={[
                          styles.optionLabel,
                          { color: selected ? colors.actionOn : colors.text },
                        ]}>
                        {option.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </Card>
          ))}

          <Input
            label="Note (optional)"
            value={note}
            onChangeText={(text) => setNote(text.slice(0, NOTE_MAX))}
            placeholder="Anything you want the group to know?"
            multiline
            numberOfLines={3}
            maxLength={NOTE_MAX}
            hint={`${note.length}/${NOTE_MAX}`}
            testID="plan-update-note"
            style={styles.noteInput}
            containerStyle={styles.noteContainer}
          />

          <ErrorBanner message={submitError} style={styles.submitError} />

          <Button
            title={submitting ? 'Sending...' : 'Send update'}
            onPress={handleSubmit}
            disabled={!hasSelection || submitting}
            loading={submitting}
            fullWidth
            testID="plan-update-submit"
          />
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    alignItems: 'center',
    paddingBottom: SPACING.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerInner: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    paddingHorizontal: SPACING.md,
  },
  centeredWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING.lg,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    alignItems: 'center',
    paddingTop: SPACING.xl,
  },
  constraint: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    paddingHorizontal: SPACING.md,
  },
  planTitle: {
    ...TYPOGRAPHY.screenTitle,
    marginBottom: SPACING.xs,
  },
  partyLine: {
    ...TYPOGRAPHY.body,
    marginBottom: SPACING.lg,
  },
  taskCard: {
    marginBottom: SPACING.smd,
    gap: SPACING.sm,
  },
  taskName: {
    ...TYPOGRAPHY.h4,
  },
  taskDescription: {
    ...TYPOGRAPHY.bodySmall,
  },
  optionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.sm,
    marginTop: SPACING.xs,
  },
  optionPill: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.full,
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.smd,
  },
  optionLabel: {
    ...TYPOGRAPHY.label,
  },
  noteContainer: {
    marginTop: SPACING.md,
  },
  noteInput: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  submitError: {
    marginTop: SPACING.sm,
  },
  doneTitle: {
    ...TYPOGRAPHY.h3,
    marginTop: SPACING.md,
    textAlign: 'center',
  },
  doneBody: {
    ...TYPOGRAPHY.body,
    marginTop: SPACING.xs,
    textAlign: 'center',
  },
  doneLinks: {
    marginTop: SPACING.lg,
    gap: SPACING.sm,
    alignItems: 'center',
  },
});
